import {Component, inject, Injector, ChangeDetectionStrategy} from "@angular/core"
import {DropFilesComponent} from "@common/components/files"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {MeshConversionError, MeshMissingUvsError, uploadMeshToGroup} from "@app/template-editor/helpers/upload"
import {UploadGqlService} from "@app/common/services/upload/upload.gql.service"
import {WebAssemblyWorkerService} from "@app/template-editor/services/webassembly-worker.service"
import {NotificationsService} from "@app/common/services/notifications/notifications.service"
import {defer, from} from "rxjs"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-drop-mesh-files",
    standalone: true,
    templateUrl: "./template-drop-mesh-files.component.html",
    styleUrls: ["./template-drop-mesh-files.component.scss"],
    imports: [DropFilesComponent],
})
export class TemplateDropMeshFilesComponent {
    private readonly injector = inject(Injector)
    private readonly notifications = inject(NotificationsService)
    uploadService = inject(UploadGqlService)
    workerService = inject(WebAssemblyWorkerService)
    sceneManagerService = inject(SceneManagerService)

    handleDroppedFiles(files: File[]) {
        if (files.length !== 1) throw new Error("Expected a single dropped file")

        const file = files[0]

        const extension = file.name.split(".").pop()?.toLowerCase()
        if (!extension) throw new Error("Failed to get extension from file name")

        if (!["cmm", "obj", "ply", "fbx"].includes(extension)) throw new Error(`Unsupported file type for mesh upload: ${extension}`)

        const action = async () => {
            try {
                const group = await uploadMeshToGroup({
                    injector: this.injector,
                    uploadService: this.uploadService,
                    workerService: this.workerService,
                    file,
                    organizationLegacyId: this.sceneManagerService.$defaultCustomerId(),
                    templateRevisionId: this.sceneManagerService.$templateRevisionId(),
                })
                if (!group) return

                this.sceneManagerService.modifyTemplateGraph((graph) => {
                    graph.parameters.nodes.addEntry(group)
                })
            } catch (error) {
                if (error instanceof MeshMissingUvsError || error instanceof MeshConversionError) {
                    this.notifications.showError(error.message, 0, "Close")
                    return
                }
                throw error
            }
        }

        this.sceneManagerService.addTask({
            description: `uploadMesh("${file.name}")`,
            task: from(defer(() => action())),
            critical: true,
        })
    }
}
